type EnvVarGetter<T> = { get: () => T };

type InternalEnvVarsResult = {
  required: () => InternalEnvVarsResult;
  modify: (
    modifyFn: (value: string | undefined) => string | undefined
  ) => InternalEnvVarsResult;
  asString: () => EnvVarGetter<string>;
  asBool: () => EnvVarGetter<boolean>;
  asNumber: () => EnvVarGetter<number>;
};

type InternalState = {
  required?: boolean;
  modifyFn?: (value: string | undefined) => string | undefined;
};

const internalEnvVars = (
  key: string,
  value: string | undefined,
  state: InternalState = {}
): InternalEnvVarsResult => {
  const required = (): InternalEnvVarsResult => {
    return internalEnvVars(key, value, { ...state, required: true });
  };

  const modify = (
    modifyFn: (value: string | undefined) => string | undefined
  ): InternalEnvVarsResult => {
    return internalEnvVars(key, value, {
      ...state,
      modifyFn,
    });
  };

  const get = (): string | undefined => {
    let res = value;

    if (state.modifyFn) {
      res = state.modifyFn(value);
    }

    if (state.required && res === undefined) {
      throw new Error(`"${key}" is a required variable, but it was not set.`);
    }

    return res;
  };

  const asString = (): EnvVarGetter<string> => {
    return {
      get: (): string => {
        const res = get();
        return res !== undefined ? String(res) : '';
      },
    };
  };

  const asBool = (): EnvVarGetter<boolean> => {
    return {
      get: (): boolean => {
        const res = get();
        return String(res) === 'true';
      },
    };
  };

  const asNumber = (): EnvVarGetter<number> => {
    return {
      get: (): number => {
        const res = get();
        return res !== undefined ? Number(res) : NaN;
      },
    };
  };

  return {
    required,
    modify,
    asString,
    asBool,
    asNumber,
  };
};

/**
 * Permet de typer et verifier la variable d'environnement donnée, plutôt que
 * d'utiliser directement process.env.MA_VARIABLE
 */
export const envVars = (key: string, value?: string): InternalEnvVarsResult =>
  internalEnvVars(key, value);
